/* Variant filtering with cascade auto-select:
   - Disables invalid radios for later options
   - If current pick is invalid OR none is picked, auto-selects the first allowed (via .click())
   - Triggers theme updates (price/media/active border) by using .click()
*/
(function () {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const initAll = () => {
      document.querySelectorAll('variant-selects').forEach(setupVariantSelects);
    };

    initAll();
    document.addEventListener('product-info:loaded', initAll);

    const observer = new MutationObserver(() => initAll());
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function setupVariantSelects(vs) {
    if (!vs || vs.__customVariantLogicBound) return;
    vs.__customVariantLogicBound = true;

    const getData = () => {
      const variantScript = vs.querySelector('script[type="application/json"][data-variants]');
      const optionsScript = vs.querySelector('script[type="application/json"][data-options]');
      const variants = variantScript ? JSON.parse(variantScript.textContent || '[]') : null;
      const options = optionsScript ? JSON.parse(optionsScript.textContent || '[]') : null;

      if (variants && options) return { variants, options };
      if (window.__productOptions?.variants?.length) return window.__productOptions;

      return {
        options: Array.from(vs.querySelectorAll('fieldset legend')).map(l => l.innerText.trim()),
        variants: []
      };
    };

    const showAvailability = vs.dataset.showAvailability === 'true';

    const getControls = () => {
      const fieldsets = Array.from(vs.querySelectorAll('fieldset'));
      return fieldsets
        .map(fieldset => ({
          fieldset,
          radios: Array.from(fieldset.querySelectorAll('input[type="radio"]'))
        }))
        .filter(c => c.radios.length);
    };

    function getSel(controls) {
      return controls.map(c => c.radios.find(r => r.checked)?.value ?? null);
    }

    function selectRadio(radio) {
      if (!radio || radio.disabled) return false;
      // Use click so theme handlers fire (updates price/media/active state)
      if (typeof radio.click === 'function') radio.click();
      else {
        radio.checked = true;
        radio.dispatchEvent(new Event('input', { bubbles: true }));
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    function styleRadio(input, disabled, unavailable) {
      input.disabled = !!disabled;
      input.classList.toggle('disabled', !!disabled);
      input.classList.toggle('visually-disabled', !!unavailable);
      let label = input.closest('label');
      if (!label && input.id) label = vs.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (label) {
        const dim = !!(disabled || unavailable);
        label.style.opacity = dim ? '0.4' : '';
        label.style.pointerEvents = disabled ? 'none' : '';
        label.style.userSelect = disabled ? 'none' : '';
        label.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        label.classList.toggle('variant-pill--disabled', dim);
      }
    }

    // Core: from a given level, enforce validity for all subsequent levels
    function refreshCascade(fromLevel = -1) {
      const data = getData();
      if (!data?.variants?.length) return;

      const controls = getControls();
      if (!controls.length) return;

      const sel = getSel(controls);

      for (let j = Math.max(fromLevel + 1, 0); j < controls.length; j++) {
        const before = (j === 0) ? [] : sel.slice(0, j);

        const allowed = new Set(
          data.variants
            .filter(v => before.every((val, k) => !val || v[`option${k + 1}`] === val))
            .map(v => v[`option${j + 1}`])
        );
        const available = new Set(
          data.variants
            .filter(v => before.every((val, k) => !val || v[`option${k + 1}`] === val))
            .filter(v => v.available)
            .map(v => v[`option${j + 1}`])
        );

        const group = controls[j];
        let current = group.radios.find(r => r.checked) || null;

        // Disable/enable radios according to allowed values
        group.radios.forEach(r => {
          const exists = allowed.size === 0 || allowed.has(r.value);
          const isDisabled = !exists;
          const isUnavailable = showAvailability && exists && !available.has(r.value);
          styleRadio(r, isDisabled, isUnavailable);
        });

        // If no current selection OR current is invalid → pick the first allowed and CLICK it
        const currentOk = current && !current.disabled;
        if (!currentOk) {
          const alt = group.radios.find(r => !r.disabled);
          if (alt && (!current || alt.value !== current.value)) {
            // click after a tiny delay to let DOM/handlers settle
            setTimeout(() => selectRadio(alt), 0);
            // Update local snapshot for subsequent levels in this loop
            sel[j] = alt.value;
          } else {
            // nothing allowed – abort cascade safely
            break;
          }
        } else {
          // keep current; ensure sel snapshot is correct for later levels
          sel[j] = current.value;
        }
      }
    }

    function getControlIndex(target) {
      const controls = getControls();
      if (!controls.length) return -1;
      const fieldset = target.closest('fieldset');
      return controls.findIndex(c => c.fieldset === fieldset);
    }

    vs.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLElement)) return;
      if (!event.target.matches('input[type="radio"]')) return;
      const idx = getControlIndex(event.target);
      setTimeout(() => refreshCascade(idx), 0);
    });

    // Initial pass: validate the entire chain from the start
    setTimeout(() => refreshCascade(-1), 0);
    // In case the theme hydrates/updates after load
    setTimeout(() => refreshCascade(-1), 300);
  }
})();
